import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import Starfield from "@/components/Starfield";
import { MEMORIAL } from "@/constants/testIds";
import { toast } from "sonner";

export default function CreateMemorial() {
  const nav = useNavigate();
  const [pet, setPet] = useState({
    pet_name: "",
    species: "",
    traits: "",
    memory: "",
    dates: "",
    pronoun: "",
    owner_display_name: "",
  });
  const [sceneryPrompt, setSceneryPrompt] = useState("");
  const [model, setModel] = useState("gemini");
  const [scenery, setScenery] = useState(null); // {image_base64, mime_type}
  const [poem, setPoem] = useState("");
  const [poemLanguage, setPoemLanguage] = useState("English");
  const [petPhoto, setPetPhoto] = useState(null); // {base64, mime}
  const [loadingScenery, setLoadingScenery] = useState(false);
  const [loadingPoem, setLoadingPoem] = useState(false);
  const [eulogy, setEulogy] = useState("");
  const [loadingEulogy, setLoadingEulogy] = useState(false);
  const [showEulogyForm, setShowEulogyForm] = useState(false);
  const [eulogyAnswers, setEulogyAnswers] = useState({
    how_they_came_into_family: "",
    three_words_personality: "",
    memorable_habit_or_quirk: "",
    what_they_loved: "",
    what_they_didnt_love: "",
    favorite_memory: "",
    what_they_taught: "",
    favorite_person: "",
    describe_to_a_stranger: "",
    if_they_could_hear_you: "",
  });
  const [saving, setSaving] = useState(false);
  const [shareToGarden, setShareToGarden] = useState(true);

  const upd = (k) => (e) => setPet((p) => ({ ...p, [k]: e.target.value }));

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Please choose a photo under 4 MB.");
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result;
      const [meta, b64] = dataUrl.split(",");
      const mime = meta.match(/data:(.*?);/)?.[1] || "image/jpeg";
      setPetPhoto({ base64: b64, mime });
    };
    r.readAsDataURL(f);
  };

  const generateScenery = async () => {
    if (!sceneryPrompt.trim() && !pet.pet_name.trim()) {
      toast.error("Add a scenery description or pet name first.");
      return;
    }
    setLoadingScenery(true);
    try {
      const { data } = await apiClient.post("/ai/scenery", {
        prompt: sceneryPrompt || `A peaceful celestial meadow for ${pet.pet_name}`,
        model,
        pet_name: pet.pet_name || null,
        reference_image_base64: petPhoto?.base64 || null,
        reference_image_mime_type: petPhoto?.mime || null,
      }, { timeout: 120000 });
      setScenery({ image_base64: data.image_base64, mime_type: data.mime_type, model: data.model });
      toast.success("Scenery painted ✦");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not generate scenery.");
    } finally {
      setLoadingScenery(false);
    }
  };

  const generateEulogy = async () => {
    const answered = Object.values(eulogyAnswers).filter((v) => v && v.trim()).length;
    if (!pet.pet_name.trim()) {
      toast.error("Their name is required first.");
      return;
    }
    if (answered === 0) {
      toast.error("Please answer at least one question so I can write something true.");
      return;
    }
    setLoadingEulogy(true);
    try {
      const { data } = await apiClient.post("/ai/eulogy", {
        pet_name: pet.pet_name,
        species: pet.species || null,
        dates: pet.dates || null,
        pronoun: pet.pronoun || null,
        language: poemLanguage,
        answers: eulogyAnswers,
      }, { timeout: 90000 });
      setEulogy(data.eulogy);
      toast.success("Their story is written ✦");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not compose the eulogy.");
    } finally {
      setLoadingEulogy(false);
    }
  };


  const generatePoem = async () => {
    if (!pet.pet_name.trim()) {
      toast.error("Tell us their name first.");
      return;
    }
    setLoadingPoem(true);
    try {
      const { data } = await apiClient.post("/ai/poem", {
        pet_name: pet.pet_name,
        species: pet.species || null,
        traits: pet.traits || null,
        memory: pet.memory || null,
        pronoun: pet.pronoun || null,
        tone: "gentle",
        language: poemLanguage,
      }, { timeout: 60000 });
      setPoem(data.poem);
      toast.success("A verse was written ✦");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not write the poem.");
    } finally {
      setLoadingPoem(false);
    }
  };

  const saveAndShare = async () => {
    if (!pet.pet_name.trim()) {
      toast.error("Their name is required.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/memorials", {
        pet_name: pet.pet_name,
        species: pet.species || null,
        dates: pet.dates || null,
        owner_display_name: pet.owner_display_name || null,
        poem: poem || null,
        eulogy: eulogy || null,
        scenery_image_base64: scenery?.image_base64 || null,
        scenery_mime_type: scenery?.mime_type || "image/png",
        pet_photo_base64: petPhoto?.base64 || null,
        pet_photo_mime_type: petPhoto?.mime || null,
        is_public: shareToGarden,
      });
      // Remember that this visitor created this memorial (their own).
      try {
        const raw = localStorage.getItem("rb_owned_memorials");
        const arr = raw ? JSON.parse(raw) : [];
        if (!arr.includes(data.share_slug)) arr.push(data.share_slug);
        localStorage.setItem("rb_owned_memorials", JSON.stringify(arr));
      } catch {/* localStorage unavailable */}
      toast.success("Memorial sealed in light ✦");
      nav(`/memorial/${data.share_slug}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not save memorial.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="relative">
      <Starfield density={30} dustDensity={10} />
      <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16">
        <div className="text-center mb-14">
          <div className="tracking-celestial text-[var(--rb-gold)] mb-3">A free digital memorial</div>
          <h1 className="font-serif-display text-5xl md:text-6xl text-[var(--rb-text-heading)] leading-tight">
            Tell us about<br/><span className="italic">your dear friend.</span>
          </h1>
          <div className="ornament mt-6"><span>✦</span></div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10">
          {/* LEFT COLUMN — Form */}
          <div className="lg:col-span-5 space-y-8">
            <div className="glass p-8 rounded-sm">
              <div className="tracking-celestial text-[var(--rb-text-muted)] mb-5">Their story</div>
              <div className="space-y-4">
                <div>
                  <label className="rb-label">Name *</label>
                  <input className="rb-input" value={pet.pet_name} onChange={upd("pet_name")} placeholder="Hazel" data-testid={MEMORIAL.petNameInput}/>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="rb-label">Species / breed</label>
                    <input className="rb-input" value={pet.species} onChange={upd("species")} placeholder="Golden retriever" data-testid={MEMORIAL.petSpeciesInput}/>
                  </div>
                  <div>
                    <label className="rb-label">Years</label>
                    <input className="rb-input" value={pet.dates} onChange={upd("dates")} placeholder="2010 — 2024"/>
                  </div>
                </div>
                <div>
                  <label className="rb-label">Their pronouns</label>
                  <select className="rb-select" value={pet.pronoun} onChange={upd("pronoun")} data-testid="memorial-pronoun-select">
                    <option value="">— let the AI infer —</option>
                    <option value="she">she / her</option>
                    <option value="he">he / him</option>
                    <option value="they">they / them</option>
                  </select>
                </div>
                <div>
                  <label className="rb-label">Their personality</label>
                  <textarea className="rb-textarea" value={pet.traits} onChange={upd("traits")} placeholder="Gentle, curious, loved chasing falling leaves..." data-testid={MEMORIAL.petTraitsInput}/>
                </div>
                <div>
                  <label className="rb-label">A cherished memory</label>
                  <textarea className="rb-textarea" value={pet.memory} onChange={upd("memory")} placeholder="The way she napped in the afternoon sunbeam..." data-testid={MEMORIAL.petMemoryInput}/>
                </div>
                <div>
                  <label className="rb-label">Your name (optional)</label>
                  <input className="rb-input" value={pet.owner_display_name} onChange={upd("owner_display_name")} placeholder="Their family"/>
                </div>
                <div>
                  <label className="rb-label">A favorite photo (optional)</label>
                  <input type="file" accept="image/*" onChange={handlePhoto} className="text-xs text-[var(--rb-text-body)] file:btn-ghost file:mr-3"/>
                  {petPhoto && (
                    <div className="mt-3 flex items-center gap-3">
                      <img src={`data:${petPhoto.mime};base64,${petPhoto.base64}`} alt="pet" className="w-16 h-16 object-cover rounded-sm border border-[var(--rb-gold-soft)]"/>
                      <button onClick={() => setPetPhoto(null)} className="text-xs text-[var(--rb-text-muted)] hover:text-[var(--rb-gold)]">remove</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="glass p-8 rounded-sm">
              <div className="tracking-celestial text-[var(--rb-text-muted)] mb-5">Conjure a scene</div>
              <label className="rb-label">Describe their heaven</label>
              <textarea
                className="rb-textarea"
                value={sceneryPrompt}
                onChange={(e) => setSceneryPrompt(e.target.value)}
                placeholder="A golden meadow at dusk, soft clouds, a single oak tree, a rainbow arching overhead..."
                data-testid={MEMORIAL.sceneryPromptInput}
              />
              <div className="flex items-center gap-3 mt-4">
                <span className="rb-label !mb-0">Painter:</span>
                <button
                  onClick={() => setModel("gemini")}
                  className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase rounded-full border transition ${model === "gemini" ? "bg-[var(--rb-gold)] text-white border-[var(--rb-gold)]" : "bg-white/60 border-[var(--rb-gold)]/30 text-[var(--rb-text-body)]"}`}
                  data-testid={MEMORIAL.modelGemini}
                >
                  Gemini Nano Banana
                </button>
                <button
                  onClick={() => setModel("openai")}
                  className={`px-4 py-2 text-[11px] tracking-[0.18em] uppercase rounded-full border transition ${model === "openai" ? "bg-[var(--rb-gold)] text-white border-[var(--rb-gold)]" : "bg-white/60 border-[var(--rb-gold)]/30 text-[var(--rb-text-body)]"}`}
                  data-testid={MEMORIAL.modelOpenai}
                >
                  OpenAI · GPT Image
                </button>
              </div>
              <button onClick={generateScenery} disabled={loadingScenery} className="btn-gold mt-5 w-full justify-center" data-testid={MEMORIAL.generateSceneryBtn}>
                {loadingScenery ? "Painting the heavens..." : "Paint the scene ✦"}
              </button>
              {petPhoto && model === "gemini" && (
                <p className="text-[11px] text-[var(--rb-gold)] mt-3 italic text-center" data-testid="scenery-photo-reference-hint">
                  ✦ Using your uploaded photo as a reference so the pet in the scene matches them
                </p>
              )}
              <p className="text-[11px] text-[var(--rb-text-muted)] mt-3 italic text-center">Image generation may take up to 60 seconds.</p>
            </div>

            <div className="glass p-8 rounded-sm">
              <div className="tracking-celestial text-[var(--rb-text-muted)] mb-5">Write the verse</div>
              <label className="rb-label">Poem language</label>
              <select
                className="rb-select"
                value={poemLanguage}
                onChange={(e) => setPoemLanguage(e.target.value)}
                data-testid="poem-language-select"
              >
                <option value="English">English</option>
                <option value="Spanish">Español · Spanish</option>
                <option value="Portuguese">Português · Portuguese</option>
                <option value="French">Français · French</option>
                <option value="Italian">Italiano · Italian</option>
                <option value="German">Deutsch · German</option>
                <option value="Dutch">Nederlands · Dutch</option>
                <option value="Tagalog">Tagalog</option>
                <option value="Japanese">日本語 · Japanese</option>
                <option value="Korean">한국어 · Korean</option>
                <option value="Chinese (Simplified)">简体中文 · Chinese</option>
              </select>
              <button onClick={generatePoem} disabled={loadingPoem} className="btn-gold w-full justify-center mt-5" data-testid={MEMORIAL.generatePoemBtn}>
                {loadingPoem ? "Writing in golden ink..." : "Compose their poem ✦"}
              </button>
              <p className="text-[11px] text-[var(--rb-text-muted)] mt-3 italic text-center">Written by Claude Sonnet 4.5, with reverence — in your chosen language.</p>
            </div>

            {/* Eulogy — Their Story */}
            <div className="glass p-8 rounded-sm" data-testid="eulogy-section">
              <div className="flex items-center justify-between mb-5">
                <div className="tracking-celestial text-[var(--rb-text-muted)]">Write their story</div>
                <button
                  type="button"
                  onClick={() => setShowEulogyForm((v) => !v)}
                  className="text-[10px] tracking-[0.2em] uppercase text-[var(--rb-gold)] hover:text-[var(--rb-text-heading)]"
                  data-testid="eulogy-toggle-btn"
                >
                  {showEulogyForm ? "Hide questions" : "Answer 10 gentle questions"}
                </button>
              </div>

              {showEulogyForm && (
                <div className="space-y-4" data-testid="eulogy-form">
                  {[
                    ["how_they_came_into_family", "How did they come into your family?", "Adoption day, first meeting…"],
                    ["three_words_personality", "Their personality in three words?", "loyal, playful, gentle…"],
                    ["memorable_habit_or_quirk", "Most memorable habit or quirk?", "The howl at the door, tail thumps…"],
                    ["what_they_loved", "What did they love more than anything?", "Snow, their sibling, walks…"],
                    ["what_they_didnt_love", "What did they NOT love? (funny fears, quirks)", "Vacuum cleaner, bath time…"],
                    ["favorite_memory", "A favorite memory you keep returning to?", "The first snowfall, road trips…"],
                    ["what_they_taught", "What did they teach your family?", "Patience, unconditional love…"],
                    ["favorite_person", "Who was their favorite person, and why?", "Someone they lit up for…"],
                    ["describe_to_a_stranger", "How would you describe them to someone who never met them?", "One or two sentences…"],
                    ["if_they_could_hear_you", "If they could hear you now, what would you say?", "The words closest to your heart…"],
                  ].map(([key, label, placeholder]) => (
                    <div key={key}>
                      <label className="rb-label">{label}</label>
                      <textarea
                        rows={2}
                        className="rb-textarea"
                        value={eulogyAnswers[key]}
                        onChange={(e) => setEulogyAnswers((p) => ({ ...p, [key]: e.target.value }))}
                        placeholder={placeholder}
                        data-testid={`eulogy-q-${key}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <button
                onClick={generateEulogy}
                disabled={loadingEulogy}
                className="btn-gold w-full justify-center mt-5"
                data-testid="eulogy-generate-btn"
              >
                {loadingEulogy ? "Weaving their story..." : "Compose their story ✦"}
              </button>
              <p className="text-[11px] text-[var(--rb-text-muted)] mt-3 italic text-center">A 300-400 word tribute — in the language chosen above.</p>

              {eulogy && (
                <div className="mt-5">
                  <label className="rb-label">Their story (edit freely)</label>
                  <textarea
                    rows={10}
                    className="rb-textarea"
                    value={eulogy}
                    onChange={(e) => setEulogy(e.target.value)}
                    data-testid="eulogy-output"
                  />
                </div>
              )}
            </div>

            <div className="glass p-6 rounded-sm">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="share-to-garden-toggle">
                <input
                  type="checkbox"
                  checked={shareToGarden}
                  onChange={(e) => setShareToGarden(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[var(--rb-gold)] cursor-pointer"
                  data-testid="share-to-garden-checkbox"
                />
                <div className="flex-1">
                  <div className="tracking-celestial text-[var(--rb-gold)] text-[11px] mb-1">Garden of Remembrance</div>
                  <div className="text-[13px] text-[var(--rb-text-body)] leading-relaxed">
                    Share {pet.pet_name?.trim() || "them"} in the public Garden of Remembrance during the free 3-day trial, so other families can see them and light a candle.
                  </div>
                  <div className="text-[11px] text-[var(--rb-text-muted)] italic mt-2">
                    You can keep this private if you prefer — the memorial page will still work by direct link.
                  </div>
                </div>
              </label>
            </div>

            <button onClick={saveAndShare} disabled={saving} className="btn-ghost w-full justify-center !text-[var(--rb-gold)] !border-[var(--rb-gold)]" data-testid={MEMORIAL.saveBtn}>
              {saving ? "Sealing in light..." : "Seal & share memorial"}
            </button>
          </div>

          {/* RIGHT COLUMN — Live preview */}
          <div className="lg:col-span-7">
            <div className="lg:sticky lg:top-8">
              <MemorialPreview pet={pet} scenery={scenery} poem={poem} petPhoto={petPhoto} eulogy={eulogy} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export function MemorialPreview({ pet, scenery, poem, petPhoto, eulogy }) {
  const hasScenery = !!scenery?.image_base64;
  return (
    <article className="memorial-card overflow-hidden" data-testid={MEMORIAL.resultPoem}>
      <div className="relative aspect-[4/3] bg-gradient-to-br from-[#FDFBF7] via-[#F2E3C6]/40 to-[#FCFCFA] flex items-center justify-center overflow-hidden">
        {hasScenery ? (
          <img src={`data:${scenery.mime_type};base64,${scenery.image_base64}`} alt="scenery" className="absolute inset-0 w-full h-full object-cover" data-testid={MEMORIAL.resultImage}/>
        ) : (
          <div className="text-center text-[var(--rb-text-muted)] font-serif-display italic px-12">
            Their celestial scene will appear here.<br/>
            <span className="text-sm">— paint it on the left.</span>
          </div>
        )}
        {petPhoto && (
          <div className="absolute bottom-5 right-5 w-24 h-24 rounded-full overflow-hidden border-2 border-[var(--rb-gold)] shadow-[0_0_30px_rgba(212,175,55,0.4)] bg-white">
            <img src={`data:${petPhoto.mime};base64,${petPhoto.base64}`} alt="pet" className="w-full h-full object-cover"/>
          </div>
        )}
      </div>
      <div className="p-10 md:p-14 text-center">
        <div className="tracking-celestial text-[var(--rb-gold)] mb-3">In loving memory</div>
        <h2 className="font-serif-display italic text-4xl md:text-5xl text-[var(--rb-text-heading)]">
          {pet.pet_name || "Their Name"}
        </h2>
        {pet.dates && <div className="font-serif-display text-[var(--rb-text-muted)] italic mt-2">{pet.dates}</div>}
        <div className="ornament mt-6 mb-8"><span>✦</span></div>
        {poem ? (
          <pre className="font-serif-display italic text-[18px] leading-[1.9] text-[var(--rb-text-heading)] whitespace-pre-wrap font-normal" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
{poem}
          </pre>
        ) : (
          <p className="font-serif-display italic text-[var(--rb-text-muted)]">
            A verse will appear here once composed.
          </p>
        )}
        {pet.owner_display_name && (
          <div className="mt-10 text-[11px] tracking-[0.25em] uppercase text-[var(--rb-text-muted)]">
            — {pet.owner_display_name} —
          </div>
        )}
      </div>
      {eulogy && <EulogyBook eulogy={eulogy} petName={pet.pet_name} />}
    </article>
  );
}

/**
 * Displays the eulogy in an open-book style — two columns on desktop, single
 * column on mobile. Warm cream paper, subtle center gutter, drop-cap.
 */
export function EulogyBook({ eulogy, petName }) {
  if (!eulogy) return null;
  return (
    <div className="relative border-t border-[var(--rb-gold)]/25" data-testid="eulogy-book">
      <div
        className="px-8 md:px-14 py-14 md:py-20"
        style={{
          background:
            "linear-gradient(180deg,#FDFBF6 0%,#F9F1DC 100%)," +
            "radial-gradient(1200px 200px at 50% 100%, rgba(212,175,55,0.06), transparent)",
        }}
      >
        <div className="text-center mb-8">
          <div className="tracking-celestial text-[var(--rb-gold)]">A quiet turn of the page</div>
          <h3 className="font-serif-display italic text-3xl md:text-4xl text-[var(--rb-text-heading)] mt-2">
            {petName ? `${petName}'s story` : "Their story"}
          </h3>
          <div className="ornament mt-4"><span>✦</span></div>
        </div>

        {/* The open-book spread */}
        <div className="relative mx-auto max-w-3xl">
          {/* Center gutter (spine) — only visible when we have 2 columns */}
          <div className="hidden md:block absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gradient-to-b from-transparent via-[var(--rb-gold)]/40 to-transparent" aria-hidden="true" />

          <div
            className="font-serif-display text-[17px] md:text-[16.5px] leading-[1.85] text-[var(--rb-text-heading)] italic first-letter:font-normal first-letter:text-[46px] first-letter:leading-[0.9] first-letter:mr-2 first-letter:float-left first-letter:mt-1 first-letter:text-[var(--rb-gold)]"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              columnCount: 2,
              columnGap: "3rem",
              columnRule: "none",
              textAlign: "justify",
              hyphens: "auto",
            }}
          >
            {eulogy.split(/\n\s*\n/).map((p, i) => (
              <p key={i} style={{ marginTop: i === 0 ? 0 : "1em", breakInside: "avoid" }}>{p}</p>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <div className="ornament"><span>✦</span></div>
        </div>
      </div>
    </div>
  );
}
